/**
 * Small line icons for the itinerary's day headers. 24px viewBox, stroke-based,
 * inherit currentColor — sized by the caller (h-3.5 w-3.5 etc).
 */
import React from 'react';

type IconProps = React.SVGProps<SVGSVGElement>;

const base = (props: IconProps) => ({
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
  ...props,
});

export const BedIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 18V6M3 14h18v4M21 14v-2a3 3 0 0 0-3-3h-7v5" />
    <circle cx="7" cy="11" r="1.8" />
  </svg>
);

export const WeatherIcon = ({ condition, ...p }: IconProps & { condition: string }) => {
  const c = (condition || '').toLowerCase();
  if (c.includes('rain') || c.includes('drizzle') || c.includes('shower') || c.includes('thunder')) {
    return (
      <svg {...base(p)}>
        <path d="M7 15a4 4 0 0 1-.5-8A5.5 5.5 0 0 1 17 8a3.5 3.5 0 0 1 0 7z" />
        <path d="M9 18l-1 2.5M13 18l-1 2.5M17 18l-1 2.5" />
      </svg>
    );
  }
  if (c.includes('clear') || c.includes('sunny')) {
    return (
      <svg {...base(p)}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4" />
      </svg>
    );
  }
  return (
    <svg {...base(p)}>
      <path d="M7 18a4.5 4.5 0 0 1-.6-9A6 6 0 0 1 18 10a4 4 0 0 1-.5 8z" />
    </svg>
  );
};

export const LongDayIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);

export const WarningWeatherIcon = ({ kind, ...p }: IconProps & { kind: string }) => {
  switch (kind) {
    case 'storm':
      return (
        <svg {...base(p)}>
          <path d="M7 15a4 4 0 0 1-.5-8A5.5 5.5 0 0 1 17 8a3.5 3.5 0 0 1 0 7" />
          <path d="M12.5 12l-2 4h3l-2 4" />
        </svg>
      );
    case 'heat':
      return (
        <svg {...base(p)}>
          <path d="M10 14.5V5a2 2 0 0 1 4 0v9.5a4 4 0 1 1-4 0z" />
          <path d="M12 9v7" />
        </svg>
      );
    case 'wind':
      return (
        <svg {...base(p)}>
          <path d="M3 9h11a2.5 2.5 0 1 0-2.5-2.5M3 13h15a2.5 2.5 0 1 1-2.5 2.5M3 17h7" />
        </svg>
      );
    case 'cold':
      return (
        <svg {...base(p)}>
          <path d="M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9M9.5 4.5L12 7l2.5-2.5M9.5 19.5L12 17l2.5 2.5" />
        </svg>
      );
    default:
      return (
        <svg {...base(p)}>
          <path d="M7 15a4 4 0 0 1-.5-8A5.5 5.5 0 0 1 17 8a3.5 3.5 0 0 1 0 7z" />
          <path d="M9 18l-1 2.5M13 18l-1 2.5M17 18l-1 2.5" />
        </svg>
      );
  }
};

export const ViewpointIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="m3 19 6-9 4 6 3-4 5 7H3Z" />
    <circle cx="16.5" cy="6.5" r="2" />
  </svg>
);

export const ArrowRightIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

export const SunsetIcon = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M17 18a5 5 0 0 0-10 0" />
    <path d="M12 9V2M4.2 10.2l1.4 1.4M1 18h2M21 18h2M18.4 11.6l1.4-1.4M23 22H1M16 5l-4 4-4-4" />
  </svg>
);
